import React, { useEffect, useState } from "react";
import Loader from "../loader/Loader";
import Navigation from "../components/Navigation";
import { useDispatch, useSelector } from "react-redux";
import "./Survey.css";
import { useNavigate, useParams } from "react-router-dom";
import * as surveyActions from "../redux/survey/survey.actions";
import { toast } from "react-toastify";
import { isUserParticipatedInSurveyAction, GET_SURVEY_QUESTIONS_RESET, getAttemptSurveyQuestionsListAction } from "../redux/attemptSurvey/attemptSurvey.actions";
import SurveyDetailsLoggedIn from "../components/SurveyDetailsLoggedIn";
import SurveyDetailsLoggedOut from "../components/SurveyDetailsLoggedOut";

const SurveyDetails = () => {
  const [data, setData] = useState({});
  const [startSurvey, setStartSurvey] = useState(false);
  const id = useParams().id;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector((state) => state.surveyInfo.loading);
  const details = useSelector((state) => state.surveyInfo.details);
  const selectedLanguage = useSelector(state => state.languageInfo.selectedLanguage);
  const profileData = useSelector(state => state.authInfo.profileData);
  const isLoggedIn = !!profileData?.data?._id;

  const clearSurveyDataFromLocalStorage = () => {
    // Clear all items related to survey questions
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('surveyQuestion_')) {
        localStorage.removeItem(key);
      }
    });
  };

  useEffect(() => {
    if (id) {
      dispatch(surveyActions.getPublicSurveyDetails(id, navigate))
      dispatch({ type: GET_SURVEY_QUESTIONS_RESET });
    }
  }, [id, dispatch, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(isUserParticipatedInSurveyAction());
      dispatch(getAttemptSurveyQuestionsListAction(id));
    }
  }, [navigate, dispatch, isLoggedIn, id]);

  const { isUserParticipatedInSurvey, surveyQuestions } = useSelector((state) => state.attemptSurveyInfo);
  const hasUserParticipated = isUserParticipatedInSurvey?.data?.filter(survey => survey._id === id)?.length > 0 ?
    isUserParticipatedInSurvey?.data?.find(survey => survey._id === id)?.users?.includes(profileData?.data?._id) :
    false;

  const participatedSurvey = isUserParticipatedInSurvey?.data?.find(survey => survey._id === id);
  const resolveSurveySteps = (stepsSource) => {
    if (!stepsSource) return null;
    const currentUserId = profileData?.data?._id;

    if (Array.isArray(stepsSource)) {
      if (!stepsSource.length) return null;
      if (currentUserId) {
        const userEntry = stepsSource.find(step =>
          step?.createdBy === currentUserId ||
          step?.userId === currentUserId ||
          step?.user?._id === currentUserId
        );
        if (userEntry) return userEntry;
      }
      return stepsSource[0];
    }

    if (typeof stepsSource === 'object') {
      if (
        !currentUserId ||
        stepsSource?.createdBy === currentUserId ||
        stepsSource?.userId === currentUserId ||
        stepsSource?.user?._id === currentUserId
      ) {
        return stepsSource;
      }
    }

    return null;
  };

  const surveyProgress =
    participatedSurvey?.user_steps ||
    resolveSurveySteps(participatedSurvey?.surveySteps) ||
    resolveSurveySteps(surveyQuestions?.data?.surveySteps) ||
    participatedSurvey?.surveySteps;

  const totalSurveyQuestions =
    Number(surveyProgress?.totalSurvey) ||
    participatedSurvey?.questions?.length ||
    data?.questions?.length ||
    data?.questionsLength ||
    0;

  const completedSurveyStep = Number(surveyProgress?.surveyStep || 0);
  const isSurveyCompleted = totalSurveyQuestions > 0 ? completedSurveyStep >= totalSurveyQuestions : false;

  // Check if current user is the organizer (createdByUser is an array)
  const userCreatedThisSurvey =
    details?.createdBy === profileData?.data?._id ||
    details?.createdByUser?.[0]?._id === profileData?.data?._id ||
    data?.createdBy === profileData?.data?._id ||
    data?.createdByUser?.[0]?._id === profileData?.data?._id;

  useEffect(() => {
    if (details && Object.keys(details).length > 0) {
      setData({ ...details }); // Create new object to avoid reference issues
    } else {
      setData({});
    }
  }, [details]);

  const handleStartSurvey = () => {
    clearSurveyDataFromLocalStorage();
    if (!isLoggedIn) {
      navigate("/auth", { state: { from: `/survey-details/${id}` } });
      const currentLanguage = localStorage.getItem("language");
      const message = currentLanguage === "Italian"
        ? "Registrati per poter partecipare ai sondaggi."
        : "Please login or register to participate in this survey.";
      toast.info(message);
    } else {
      if (userCreatedThisSurvey) {
        toast.info("You cannot participate in your own survey.");
      } else {
        setStartSurvey(prevState => !prevState); // Toggle using callback to ensure latest state
      }
    }
  };

  const ContributedSurvey = isUserParticipatedInSurvey?.data?.filter(survey => {
    if (!survey.preliminary) return true;
    if (survey.preliminaryQuestionType !== "direct elimination") return true;
    return survey.preliminaryResult !== "Fail";
  }) || [];

  return (
    <>
      <Navigation />
      {loading ? (
        <Loader />
      ) : (
        <>
          {
            data && Object.keys(data).length > 0 ? (
              isLoggedIn ? (
                <SurveyDetailsLoggedIn
                  data={data}
                  id={id}
                  startSurvey={startSurvey}
                  handleStartSurvey={handleStartSurvey}
                  hasUserParticipated={hasUserParticipated}
                  userCreatedThisSurvey={userCreatedThisSurvey}
                  details={details}
                  selectedLanguage={selectedLanguage}
                  ContributedSurvey={ContributedSurvey}
                  surveyQuestions={surveyQuestions}
                  isSurveyCompleted={isSurveyCompleted}
                  surveyProgress={surveyProgress}
                />
              ) : (
                <SurveyDetailsLoggedOut
                  data={data}
                  id={id}
                  isLoggedIn={isLoggedIn}
                  startSurvey={startSurvey}
                  handleStartSurvey={handleStartSurvey}
                  hasUserParticipated={hasUserParticipated}
                  details={details}
                  selectedLanguage={selectedLanguage}
                />
              )
            ) : (
              <h2 className="text-muted text-center mt-3">No Data Found</h2>
            )
          }
        </>
      )}
    </>
  );
};

export default SurveyDetails;
